import type { UserPublic } from '@runway/shared';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ApiError } from '../../net/api';
import { useHub } from '../../state/hub';
import { useSession } from '../../state/session';
import { Button } from '../../ui/Button';
import { Spinner } from '../../ui/Spinner';
import { toast } from '../../ui/Toast';
import { UserAvatar } from './UserAvatar';

type Relation = 'friend' | 'incoming' | 'outgoing' | 'me' | null;

/**
 * Search-as-you-type against /api/users/search with one-click "Add"; pressing
 * Enter sends a request for whatever was typed (exact name or email).
 */
export function AddFriendBox({ autoFocus = false }: { autoFocus?: boolean }) {
  const me = useSession((s) => s.user);
  const searchUsers = useHub((s) => s.searchUsers);
  const requestFriend = useHub((s) => s.requestFriend);
  const acceptFriend = useHub((s) => s.acceptFriend);
  const friends = useHub((s) => s.friends);
  const incoming = useHub((s) => s.incoming);
  const outgoing = useHub((s) => s.outgoing);

  const [q, setQ] = useState('');
  const [hits, setHits] = useState<UserPublic[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchUnavailable, setSearchUnavailable] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // Debounced search.
  useEffect(() => {
    const query = q.trim();
    abortRef.current?.abort();
    if (query.length < 2 || searchUnavailable) {
      setHits(null);
      setSearching(false);
      return;
    }
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const users = await searchUsers(query, ctrl.signal);
        if (!ctrl.signal.aborted) setHits(users);
      } catch (err) {
        if (ctrl.signal.aborted) return;
        if (err instanceof ApiError && (err.status === 404 || err.status === 501)) setSearchUnavailable(true);
        setHits([]);
      } finally {
        if (!ctrl.signal.aborted) setSearching(false);
      }
    }, 250);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [q, searchUsers, searchUnavailable]);

  const relationOf = (userId: string): Relation => {
    if (userId === me?.id) return 'me';
    if (friends.some((f) => f.id === userId)) return 'friend';
    if (incoming.some((r) => r.from.id === userId)) return 'incoming';
    if (outgoing.some((r) => r.to.id === userId)) return 'outgoing';
    return null;
  };

  const add = async (user: UserPublic) => {
    setBusyId(user.id);
    try {
      const rel = relationOf(user.id);
      if (rel === 'incoming') {
        const req = incoming.find((r) => r.from.id === user.id);
        if (req) await acceptFriend(req.id);
        toast.success(`You and ${user.displayName} are now friends!`);
      } else {
        await requestFriend(user.displayName);
        toast.success(`Request sent to ${user.displayName}`);
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not send the request');
    } finally {
      setBusyId(null);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const query = q.trim();
    if (query.length < 2) return;
    // If there is exactly one addable hit, add that one.
    const addable = (hits ?? []).filter((h) => relationOf(h.id) === null || relationOf(h.id) === 'incoming');
    if (addable.length === 1 && addable[0]) {
      await add(addable[0]);
      setQ('');
      return;
    }
    setSubmitting(true);
    try {
      const res = await requestFriend(query);
      if (res && 'friendshipId' in res) toast.success(`You and ${res.displayName} are now friends!`);
      else toast.success(`Request sent to ${query}`);
      setQ('');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Could not send the request';
      toast.error(err instanceof ApiError && err.status === 404 ? `Nobody called “${query}” yet — check the spelling or their email.` : msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="add-friend stack stack-sm" onSubmit={submit} role="search" aria-label="Add a friend">
      <label className="field">
        <span className="label">Add a friend by name or email</span>
        <div className="row" style={{ flexWrap: 'nowrap' }}>
          <input
            className="input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="e.g. Zoe or zoe@example.com"
            autoComplete="off"
            autoFocus={autoFocus}
            aria-autocomplete="list"
            aria-controls="add-friend-results"
            enterKeyHint="send"
          />
          <Button type="submit" variant="gold" loading={submitting} disabled={q.trim().length < 2}>
            Add
          </Button>
        </div>
      </label>
      {q.trim().length >= 2 ? (
        <div id="add-friend-results" className="add-friend-results" aria-live="polite">
          {searching && hits === null ? (
            <div className="row small muted" style={{ padding: '6px 10px' }}>
              <Spinner size={14} /> Searching…
            </div>
          ) : null}
          {hits && hits.length === 0 && !searching && !searchUnavailable ? (
            <div className="small muted" style={{ padding: '6px 10px' }}>
              No one found — press <span className="kbd">Enter</span> to try the exact name or email anyway.
            </div>
          ) : null}
          {(hits ?? []).map((u) => {
            const rel = relationOf(u.id);
            return (
              <div key={u.id} className="add-friend-hit">
                <UserAvatar displayName={u.displayName} picture={u.picture} avatar={u.avatar} size="sm" />
                <strong>{u.displayName}</strong>
                {rel === 'me' ? (
                  <span className="chip">That’s you</span>
                ) : rel === 'friend' ? (
                  <span className="chip chip-success">Friends</span>
                ) : rel === 'outgoing' ? (
                  <span className="chip">Requested</span>
                ) : (
                  <Button size="sm" variant={rel === 'incoming' ? 'gold' : 'primary'} loading={busyId === u.id} onClick={() => void add(u)}>
                    {rel === 'incoming' ? 'Accept' : 'Add'}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      ) : null}
    </form>
  );
}
